import React, { useState, useRef, useEffect, useCallback } from "react";
import { useMeeting } from "../../config/sdkConfig";
import { useAgoraRTM } from "../../context/AgoraRTMContext";
import chatBg from "../../images/branding/chat-background.png";
import "./ChatPanel.css";

// Helper function to get user initials
function getUserInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

// Convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
  const parts = text.split(urlRegex);
  const testRegex =
    /^(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/;
  return parts.map((part, index) => {
    if (part && testRegex.test(part)) {
      let href = part;
      if (!part.startsWith('http://') && !part.startsWith('https://')) {
        href = `https://${part}`;
      }
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline break-all cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Generate consistent color based on name
function getColorFromName(name) {
  const colors = ['#00c853', '#6a1fbf', '#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Format timestamp
function formatTime(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
}

export function AgoraRTMChatPanel({ panelHeight }) {
  const mMeeting = useMeeting();
  const {
    isLoading,
    isLoggedIn,
    messages,
    typingUsers,
    sendMessage,
    sendTypingIndicator,
  } = useAgoraRTM();

  const [inputText, setInputText] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle input change with typing indicator
  const handleInputChange = useCallback((e) => {
    setInputText(e.target.value);
    sendTypingIndicator();
  }, [sendTypingIndicator]);

  // Handle send message
  const handleSend = useCallback(async () => {
    if (!inputText.trim()) return;

    const success = await sendMessage(inputText);
    if (success) {
      setInputText("");
      inputRef.current?.focus();
    }
  }, [inputText, sendMessage]);

  // Handle key press
  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center text-white bg-black"
        style={{ height: panelHeight }}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#8e01da]"></div>
          <p className="text-sm text-gray-400">Connecting to chat...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div
        className="flex flex-col items-center justify-center text-white bg-black"
        style={{ height: panelHeight, padding: "20px" }}
      >
        <p style={{ marginBottom: "10px", fontSize: "16px" }}>
          Unable to connect to chat
        </p>
        <p style={{ fontSize: "12px", color: "#999", textAlign: "center" }}>
          Please refresh the page to try again
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "15px",
            padding: "8px 16px",
            backgroundColor: "#6a1fbf",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        height: panelHeight,
        backgroundImage: `url(${chatBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
      }}
      className="custom-chat-panel w-full"
    >
      {/* Chat Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>Group Chat</span>
          <span style={{ color: "#6b7280", fontSize: "12px" }}>
            ({mMeeting?.participants?.size || 1} participants)
          </span>
        </div>
      </div>

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {messages.length === 0 ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#6b7280",
            textAlign: "center",
            padding: "20px",
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: "12px", opacity: 0.5 }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={{ fontSize: "14px", marginBottom: "4px" }}>No messages yet</p>
            <p style={{ fontSize: "12px" }}>Be the first to say something!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: "flex",
                flexDirection: msg.isLocal ? "row-reverse" : "row",
                gap: "8px",
                alignItems: "flex-start",
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "12px",
                  flexShrink: 0,
                  background: getColorFromName(msg.userName),
                }}
              >
                {getUserInitials(msg.userName)}
              </div>

              {/* Message Bubble */}
              <div
                style={{
                  maxWidth: "70%",
                  background: msg.isLocal ? "#6a1fbf" : "rgba(255,255,255,0.1)",
                  borderRadius: msg.isLocal ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  padding: "8px 12px",
                }}
              >
                {/* Name and Time */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                  gap: "8px",
                }}>
                  <span style={{
                    color: msg.isLocal ? "rgba(255,255,255,0.8)" : "#9ca3af",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}>
                    {msg.isLocal ? "You" : msg.userName}
                  </span>
                  <span style={{
                    color: msg.isLocal ? "rgba(255,255,255,0.6)" : "#6b7280",
                    fontSize: "10px",
                  }}>
                    {formatTime(msg.timestamp)}
                  </span>
                </div>

                {/* Message Text */}
                <p style={{
                  color: "#fff",
                  fontSize: "14px",
                  margin: 0,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}>
                  {renderTextWithLinks(msg.text)}
                </p>
              </div>
            </div>
          ))
        )}

        {/* Typing Indicator - always rendered to reserve space */}
        <div style={{
          color: "#9ca3af",
          fontSize: "12px",
          fontStyle: "italic",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          height: "18px",
          lineHeight: "18px",
          flexShrink: 0,
          visibility: typingUsers.length > 0 ? "visible" : "hidden",
        }}>
          {typingUsers.length > 0 && (
            <>
              {typingUsers.slice(0, 2).join(", ")}
              {typingUsers.length > 2 && ` and ${typingUsers.length - 2} others`}
              {typingUsers.length === 1 ? " is" : " are"} typing...
            </>
          )}
          {typingUsers.length === 0 && "\u00A0"}
        </div>

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        style={{
          background: "#3A3B3B",
          borderRadius: "24px 24px 0 0",
          padding: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "24px",
            padding: "4px 4px 4px 16px",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            name="rlink-chat-message"
            autoComplete="off"
            data-form-type="other"
            value={inputText}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#fff",
              fontSize: "14px",
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim()}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              border: "none",
              background: inputText.trim() ? "#6a1fbf" : "rgba(255,255,255,0.1)",
              cursor: inputText.trim() ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke={inputText.trim() ? "#fff" : "#6b7280"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AgoraRTMChatPanel;
